import type { useApiFetch } from '@/features/auth'
import { buildQueryString } from '@/lib/buildQueryString'
import type {
  InvestorCase,
  InvestorDetail,
  InvestorListResult,
  InvestorMe,
  InvestorReveal,
  InvestorsListParams,
} from '@/features/investors/types'

type AuthedFetch = ReturnType<typeof useApiFetch>

export function fetchInvestors(
  authedFetch: AuthedFetch,
  params: InvestorsListParams,
): Promise<InvestorListResult> {
  return authedFetch<InvestorListResult>(`/api/v1/investors${buildQueryString(params)}`)
}

/** Mirrors `CreateInvestorDto`. `POST /investors` is idempotent match-or-create by PAN then
 * mobile — it never 409s on a duplicate, it upserts/attaches to the existing investor row. */
export interface CreateInvestorInput {
  name: string
  email: string
  mobile: string
  pan?: string
  city?: string
}

export function createInvestor(
  authedFetch: AuthedFetch,
  input: CreateInvestorInput,
): Promise<InvestorDetail> {
  return authedFetch<InvestorDetail>('/api/v1/investors', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function fetchInvestorDetail(
  authedFetch: AuthedFetch,
  investorId: string,
): Promise<InvestorDetail> {
  return authedFetch<InvestorDetail>(`/api/v1/investors/${investorId}`)
}

/** Mirrors `UpdateInvestorDto` — all optional; the backend 422s on an all-blank body. `age` is the
 * only UI path to set it for an Add-Lead-created Build Portfolio case. */
export interface UpdateInvestorInput {
  name?: string
  email?: string
  mobile?: string
  pan?: string
  city?: string
  age?: number
}

export function updateInvestorDetail(
  authedFetch: AuthedFetch,
  investorId: string,
  input: UpdateInvestorInput,
): Promise<InvestorDetail> {
  return authedFetch<InvestorDetail>(`/api/v1/investors/${investorId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** The "Edit investor details" dialog's real-value fetch — staff only, never called on normal
 * page load. */
export function fetchInvestorReveal(
  authedFetch: AuthedFetch,
  investorId: string,
): Promise<InvestorReveal> {
  return authedFetch<InvestorReveal>(`/api/v1/investors/${investorId}/reveal`)
}

export function fetchInvestorCases(
  authedFetch: AuthedFetch,
  investorId: string,
): Promise<InvestorCase[]> {
  return authedFetch<InvestorCase[]>(`/api/v1/investors/${investorId}/cases`)
}

export function fetchMyInvestorProfile(authedFetch: AuthedFetch): Promise<InvestorMe> {
  return authedFetch<InvestorMe>('/api/v1/investors/me')
}

/** Mirrors `UpdateInvestorMeDto` — deliberately narrower than `UpdateInvestorInput`: no
 * `email`/`mobile` at all. The global `ValidationPipe`'s `forbidNonWhitelisted: true` means
 * sending either is a 422, not a silently-dropped field. */
export interface UpdateMyInvestorProfileInput {
  name?: string
  city?: string
}

export function updateMyInvestorProfile(
  authedFetch: AuthedFetch,
  input: UpdateMyInvestorProfileInput,
): Promise<InvestorMe> {
  return authedFetch<InvestorMe>('/api/v1/investors/me', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export interface UpdateNotificationPreferencesInput {
  notifyReportReady?: boolean
  notifyCallSummaries?: boolean
  notifyServiceRecommendations?: boolean
}

export function updateMyNotificationPreferences(
  authedFetch: AuthedFetch,
  input: UpdateNotificationPreferencesInput,
): Promise<InvestorMe> {
  return authedFetch<InvestorMe>('/api/v1/investors/me/notification-preferences', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function fetchMyInvestorCases(authedFetch: AuthedFetch): Promise<InvestorCase[]> {
  return authedFetch<InvestorCase[]>('/api/v1/investors/me/cases')
}
